import { Zap, Heart, Clock, Shield, Snowflake, Star, Crosshair } from 'lucide-react';
import { PowerUpType, PowerUpInfo } from '../types';

const PowerUpIcon = ({ type, size = 'w-5 h-5' }: { type: PowerUpType; size?: string }) => {
  switch (type) {
    case 'speed': return <Zap className={`${size} text-yellow-400`} />;
    case 'health': return <Heart className={`${size} text-red-500`} />;
    case 'slowZombies': return <Clock className={`${size} text-blue-400`} />;
    case 'doubleDamage': return <Shield className={`${size} text-purple-400`} />;
    case 'shield': return <Shield className={`${size} text-cyan-400`} />;
    case 'rapidFire': return <Crosshair className={`${size} text-orange-400`} />;
    case 'freezeZombies': return <Snowflake className={`${size} text-blue-200`} />;
    case 'scoreMultiplier': return <Star className={`${size} text-yellow-300`} />;
    default: return null;
  }
};

const powerUpData: PowerUpInfo[] = [
  {
    type: 'health',
    name: 'Health Restore',
    description: 'Instantly restores 1 health point (max 5)',
    rarity: 'Common'
  },
  {
    type: 'speed',
    name: 'Speed Boost',
    description: 'Increases movement speed by 50%',
    duration: '10s',
    rarity: 'Uncommon'
  },
  {
    type: 'slowZombies',
    name: 'Slow Zombies',
    description: 'Reduces zombie movement speed by 50%',
    duration: '15s',
    rarity: 'Uncommon'
  },
  {
    type: 'doubleDamage',
    name: 'Double Damage',
    description: 'Doubles all damage dealt to zombies',
    duration: '10s',
    rarity: 'Uncommon'
  },
  {
    type: 'shield',
    name: 'Shield',
    description: 'Reduces damage taken chance to 30%',
    duration: '15s',
    rarity: 'Rare'
  },
  {
    type: 'rapidFire',
    name: 'Rapid Fire',
    description: 'Reduces gun cooldown by 70%',
    duration: '12s',
    rarity: 'Rare'
  },
  {
    type: 'freezeZombies',
    name: 'Freeze Zombies',
    description: 'Slows zombies to 10% of normal speed',
    duration: '8s',
    rarity: 'Very Rare'
  },
  {
    type: 'scoreMultiplier',
    name: 'Score Multiplier',
    description: 'Doubles all points earned',
    duration: '20s',
    rarity: 'Very Rare'
  }
];

const getRarityColor = (rarity: string) => {
  switch (rarity) {
    case 'Common': return 'text-gray-300';
    case 'Uncommon': return 'text-green-400';
    case 'Rare': return 'text-blue-400';
    case 'Very Rare': return 'text-purple-400';
    default: return 'text-gray-300';
  }
};

const PowerUpGlossary = () => {
  return (
    <div className="w-full max-w-6xl mx-auto mt-8 bg-gray-900 rounded-lg border-2 border-gray-700 p-6">
      <h2 className="text-2xl font-bold text-center mb-6 text-yellow-400">
        🎮 POWER-UP GLOSSARY
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {powerUpData.map((powerUp) => (
          <div 
            key={powerUp.type}
            className="bg-gray-800 rounded-lg p-4 border border-gray-600 hover:border-gray-500 transition-colors"
          >
            <div className="flex items-center gap-3 mb-2">
              <PowerUpIcon type={powerUp.type} size="w-6 h-6" />
              <div>
                <h3 className="font-bold text-white text-sm">{powerUp.name}</h3>
                <span className={`text-xs ${getRarityColor(powerUp.rarity)}`}>
                  {powerUp.rarity}
                </span>
              </div>
            </div>
            
            <p className="text-gray-300 text-xs mb-2">
              {powerUp.description}
            </p>
            
            {powerUp.duration && (
              <div className="text-xs text-yellow-300 font-mono">
                Duration: {powerUp.duration}
              </div>
            )}
          </div>
        ))}
      </div>
      
      <div className="mt-6 text-center">
        <h3 className="text-lg font-bold text-white mb-2">🎯 WEAPON UNLOCKS</h3>
        <div className="flex justify-center gap-8 text-sm">
          <div className="text-gray-300">
            <span className="text-yellow-400">⚔️ Sword:</span> Starting weapon
          </div>
          <div className="text-gray-300">
            <span className="text-red-400">🔫 Gun:</span> Unlocks at 500 points
          </div>
          <div className="text-gray-300">
            <span className="text-orange-400">🪓 Axe:</span> Unlocks at 1000 points
          </div>
          <div className="text-gray-300">
            <span className="text-red-500">🔴 Laser:</span> Unlocks at 2000 points
          </div>
          <div className="text-gray-300">
            <span className="text-orange-500">💣 Grenade:</span> Unlocks at 5000 points
          </div>
        </div>
      </div>
    </div>
  );
};

export default PowerUpGlossary;
